import React, { useCallback, useMemo, useRef } from "react";
import { useListProps } from "./utilities/list";
import { ListProvider } from "../../Components/common/List/Providers/List";
import { List } from "../../Components/common/List/List";
import { Company } from "../../Models/Company";
import { useAppContext } from "../../AppProvider";
import { useTypes } from "../../utilities/types";
import { useIndexCompaniesContext } from "./providers/Index";
import { createModalConfig, ModalConfig } from "../../Models/ModalConfig";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faTrash } from "../../utilities/fontawesomeIcons";
import Delete from "./Delete.tsx";
import { DeleteCompanyProvider } from "./providers/Delete";
import { useNavigate } from "react-router-dom";

const AnyListProvider = ListProvider as any;
const AnyList = List as any;

const Companies = () => {
  const listRef = useRef<{ refresh: () => void } | null>(null);
  const listProps = useListProps();
  const { modalConfig, setModalConfig } = useIndexCompaniesContext();

  const renderItem = useCallback((item: Company) => {
    return <ListItem item={item} />;
  }, []);

  const closeModal = () => {
    setModalConfig(createModalConfig());
  };

  return (
    <React.Fragment>
      <AnyListProvider
        path={listProps.path}
        sorting={listProps.sorting}
        filters={listProps.filters}
      >
        <AnyList
          ref={listRef}
          title={listProps.title}
          pagination={listProps.pagination}
          renderItem={renderItem}
          banner={listProps.banner}
          create={listProps.create}
          massiveActions={listProps.massiveActions}
        />
      </AnyListProvider>

      <Modal isOpen={modalConfig.isOpen} size={modalConfig.size}>
        {modalConfig.action !== "delete" && (
          <ModalHeader toggle={closeModal}>
            <span className="modal-title">
              <i className={modalConfig.className + " me-2"}></i>
              {modalConfig.title}
            </span>
          </ModalHeader>
        )}
        <ModalBody className={modalConfig.className || ""}>
          {modalConfig.action === "delete" && (
            <DeleteCompanyProvider
              data={modalConfig.data}
              callback={() => {
                listRef.current?.refresh();
                closeModal();
              }}
            >
              <Delete />
            </DeleteCompanyProvider>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default Companies;

const ListItem: React.FC<{ item: Company }> = React.memo(({ item }) => {
  const { dictionary } = useAppContext();
  const { companies } = useTypes();
  const context = useIndexCompaniesContext();
  const companyColor = useMemo(
    () => companies.find((c) => c.value === item.type)?.color,
    [item]
  );

  const navigate = useNavigate();

  const openModal = useCallback(
    (
      action: ModalConfig["action"],
      data?: string[],
      size: ModalConfig["size"] = "sm",
      className = "",
      title = ""
    ) => {
      context?.setModalConfig({
        isOpen: true,
        action,
        size,
        data,
        className,
        title,
      });
    },
    [context]
  );

  return (
    <div
      className="list-item px-2"
      style={{ borderLeft: `5px ${companyColor} solid` }}
    >
      <div
        className="d-grid grid-column align-items-center"
        style={{ gridTemplateColumns: "0.5fr 1fr 1fr 1fr 1fr 1fr" }}
      >
        <div className="text-center">
          {item.logoDarkUrl && <img src={item.logoDarkUrl} alt="Logo" />}
        </div>
        <div>
          <label className="label-2">
            {dictionary.registries.company_name}
          </label>
          <label className="label-3">{item.name || "-"}</label>
        </div>
        <div>
          <label className="label-2">
            {dictionary.registries.commercial_name}
          </label>
          <label className="label-3">{item.friendlyName || "-"}</label>
        </div>
        <div>
          <label className="label-2">
            {dictionary.references.external_reference}
          </label>
          <label className="label-3">{item.externalReference || "-"}</label>
        </div>
        <div>
          <label className="label-2">{dictionary.registries.vat_number}</label>
          <label className="label-3">{item.vatNumber || "-"}</label>
        </div>
        <div className="text-end">
          <button
            title={dictionary.actions.edit}
            className="button-1 button-1-light button-1-icon me-1"
            onClick={() => navigate(`edit/${item.id}`)}
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
          <button
            title={dictionary.actions.delete}
            className="button-1 button-1-danger button-1-icon"
            onClick={() => {
              openModal(
                "delete",
                [item.id],
                "sm",
                "",
                dictionary.actions.delete
              );
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </div>
    </div>
  );
});
